import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import './HomePage.css';

function HomePage(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [showFloatingNav, setShowFloatingNav] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const navRef = useRef<HTMLElement>(null);

  const handleCopyEmail = () => {
    navigator.clipboard.writeText('info@sincerementalhealth.com');
    setEmailCopied(true);
    setTimeout(() => setEmailCopied(false), 2000);
  };

  useEffect(() => {
    // Handle anchor scrolling on page load
    const hash = window.location.hash;
    if (hash) {
      const element = document.querySelector(hash);
      if (element) {
        // Small delay to ensure page is fully rendered
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }

    // Handle floating navigation visibility
    const handleScroll = () => {
      const heroHeight = window.innerHeight;
      setShowFloatingNav(window.scrollY > heroHeight / 2);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node) && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mobileMenuOpen]);
  return (
    <div className="home-page">
      <nav ref={navRef} className={`floating-nav ${showFloatingNav ? 'floating-nav--visible' : ''}`}>
        <div className="floating-nav-content">
          <div className="nav-logo">
            <img src="/logo.png" alt="Sincere" className="nav-logo-img" />            
          </div>
          <button 
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
          <ul className={`nav-menu ${mobileMenuOpen ? 'nav-menu--open' : ''}`}>
            <li><a href="#features" className="nav-link" onClick={() => setMobileMenuOpen(false)}>{t('navigation.features')}</a></li>
            <li><a href="#download" className="nav-link" onClick={() => setMobileMenuOpen(false)}>{t('navigation.download')}</a></li>
            <li><a href="#faq" className="nav-link" onClick={() => setMobileMenuOpen(false)}>{t('navigation.faq')}</a></li>
            <li><a href="#about" className="nav-link" onClick={() => setMobileMenuOpen(false)}>{t('navigation.about')}</a></li>
            <li><a href="#contact" className="nav-link" onClick={() => setMobileMenuOpen(false)}>{t('navigation.contact')}</a></li>
            <li className="nav-lang"><LanguageSwitcher /></li>
          </ul>
        </div>
      </nav>
      <header className="hero-section">
        <div className="hero-lang-switcher">
          <LanguageSwitcher />
        </div>
        <div className="hero-content">
          <div className="hero-logo">
            <img src="/logo.png" alt="Sincere Logo" className="logo" />
          </div>
          <h1 className="hero-title">{t('hero.title')}</h1>
          <p className="hero-subtitle">{t('hero.subtitle')}</p>
          <div className="hero-actions">
            <a
              href={t('hero.betaUrl')}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-beta"
            >
              {t('hero.joinBeta')}
            </a>
            <a href="#features" className="btn btn-learn-more">{t('hero.learnMore')}</a>
          </div>
        </div>
        <div className="hero-visual">
          <div className="brand-shapes">
            <div className="shape shape-1"></div>
            <div className="shape shape-2"></div>
            <div className="shape shape-3"></div>
          </div>
        </div>
      </header>

      <section className="features-section" id="features">
        <div className="container">
          <h2 className="section-title">{t('features.title')}</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon icon-secure">
                <svg viewBox="0 0 512 512" fill="white" width="40" height="40">
                  <path d="M336 176L225.2 286.2l-56.8-56.8-31.2 31.2 88 88 142-142z"/>
                  <path d="M464 128.9L256 32L48 128.9v114.3c0 143.8 91.9 265.4 216 308.8 124.1-43.4 216-165 216-308.8V128.9zM256 446.5c-97.2-37.5-176-137.7-176-246.1V165.8L256 80l176 85.8v34.6c0 108.4-78.8 208.6-176 246.1z"/>
                </svg>
              </div>
              <h3>{t('features.secure.title')}</h3>
              <p>{t('features.secure.description')}</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon icon-intelligent">
                <svg viewBox="0 0 512 512" fill="none" stroke="white" strokeWidth="32" width="40" height="40">
                  <path d="M304 384v-24c0-29 31.54-56.43 52-76 28.84-27.57 44-64.61 44-108 0-80-63.73-144-144-144a143.6 143.6 0 00-144 144c0 41.84 15.81 81.39 44 108 20.35 19.21 52 46.7 52 76v24M224 480h64M208 432h96M256 384V256" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>{t('features.intelligent.title')}</h3>
              <p>{t('features.intelligent.description')}</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon icon-trusted">
                <svg viewBox="0 0 512 512" fill="white" width="40" height="40">
                  <path d="M352.92 80C288 80 256 144 256 144s-32-64-96.92-64c-52.76 0-94.54 44.14-95.08 96.81-1.1 109.33 86.73 187.08 183 252.42a16 16 0 0018 0c96.26-65.34 184.09-143.09 183-252.42-.54-52.67-42.32-96.81-95.08-96.81z" stroke="white" strokeWidth="32" fill="none"/>
                </svg>
              </div>
              <h3>{t('features.trusted.title')}</h3>
              <p>{t('features.trusted.description')}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="values-section">
        <div className="container">
          <div className="values-content">
            <div className="values-text">
              <h2>{t('values.title')}</h2>
              <p>{t('values.description')}</p>
              <ul className="values-list">
                {(t('values.list', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
            <div className="values-visual">
              <div className="gradient-card">
                <h3 className="stats-title">{t('values.statistics.title')}</h3>
                <div className="stats-grid">
                  {(t('values.statistics.stats', { returnObjects: true }) as Array<{number: string, label: string}>).map((stat, index) => (
                    <div key={index} className="stat-item">
                      <div className="stat-number">{stat.number}</div>
                      <div className="stat-label">{stat.label}</div>
                    </div>
                  ))}
                </div>
                <p className="stats-source">
                  {t('values.statistics.source')} <a href={t('values.statistics.sourceUrl')} target="_blank" rel="noopener noreferrer" className="stats-source-link">{t('values.statistics.sourceLink')}</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="download-section" id="download">
        <div className="container">
          <div className="download-content">
            <h2 className="section-title">{t('download.title')}</h2>
            <p className="download-subtitle">{t('download.subtitle')}</p>
            <div className="download-buttons">
              <button className="app-store-btn" onClick={() => alert('App Store link coming soon!')}>
                <div className="store-btn-content">
                  <div className="store-icon apple-icon"></div>
                  <div className="store-text">
                    <span className="download-text">{t('download.appStore.prefix')}</span>
                    <span className="store-name">{t('download.appStore.name')}</span>
                  </div>
                </div>
              </button>
              <button className="app-store-btn" onClick={() => alert('Google Play link coming soon!')}>
                <div className="store-btn-content">
                  <div className="store-icon google-icon"></div>
                  <div className="store-text">
                    <span className="download-text">{t('download.googlePlay.prefix')}</span>
                    <span className="store-name">{t('download.googlePlay.name')}</span>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="faq-section" id="faq">
        <div className="container">
          <h2 className="section-title">{t('faq.title')}</h2>
          <div className="faq-accordion">
            {(t('faq.questions', { returnObjects: true }) as Array<{question: string, answer: string}>).map((item, index) => (
              <div key={index} className={`faq-item ${openFaqIndex === index ? 'faq-item--open' : ''}`}>
                <button
                  className="faq-question"
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  aria-expanded={openFaqIndex === index}
                >
                  <span>{item.question}</span>
                  <span className="faq-icon">{openFaqIndex === index ? '−' : '+'}</span>
                </button>
                <div className="faq-answer-wrapper">
                  <p className="faq-answer">{item.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="about-section" id="about">
        <div className="container">
          <h2 className="section-title">{t('about.title')}</h2>
          <div className="about-content">
            <div className="about-text">
              <p>{t('about.description1')}</p>
              <p>{t('about.description2')}</p>
            </div>
            
            <div className="about-values">
              {(t('about.values', { returnObjects: true }) as Array<{title: string, description: string}>).map((item, index) => {
                const icons = ['🔒', '🧠', '👥', '📱'];
                return (
                  <div key={index} className="value-item">
                    <div className="value-icon">{icons[index]}</div>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="contact-section" id="contact">
        <div className="container">
          <h2 className="section-title">{t('contact.title')}</h2>
          <div className="contact-simple">
            <p>{t('contact.subtitle')}</p>
            <div className="contact-email-wrapper">
              <a href="mailto:info@sincerementalhealth.com" className="contact-email">info@sincerementalhealth.com</a>
              <button
                onClick={handleCopyEmail}
                className="copy-email-btn"
                aria-label="Copy email"
              >
                {emailCopied ? '✓' : '📋'}
              </button>
            </div>
            {emailCopied && <span className="copied-message">Copied!</span>}
          </div>
        </div>
      </section>

      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <img src="/logo.png" alt="Sincere Logo" className="footer-logo" />
              <p>{t('footer.brand')}</p>
            </div>
            <div className="footer-links">
              <div className="footer-column">
                <h4>{t('footer.platform')}</h4>
                <ul>
                  <li><a href="#features">{t('footer.aiFeatures')}</a></li>
                  <li><a href="#integration">{t('footer.integration')}</a></li>
                  <li><a href="#security">{t('footer.securityPrivacy')}</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>{t('footer.resources')}</h4>
                <ul>
                  <li><a href="#faq">{t('navigation.faq')}</a></li>
                  <li><a href="#contact">{t('footer.contactUs')}</a></li>
                  <li><Link to={i18n.language === 'pt' ? '/terms-pt' : '/terms'}>{t('footer.termsConditions')}</Link></li>
                  <li><Link to={i18n.language === 'pt' ? '/privacy-pt' : '/privacy'}>{t('footer.privacyPolicy')}</Link></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>{t('footer.followUs')}</h4>
                <ul>
                  <li>
                    <a href="https://www.instagram.com/sincerementalhealth/" target="_blank" rel="noopener noreferrer" className="footer-social-link">
                      <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                      </svg>
                      <span>{t('footer.instagram')}</span>
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>© 2026 Sincere. All rights reserved.</p>
          </div>
        </div>
      </footer>

      <a
        href={t('hero.betaUrl')}
        target="_blank"
        rel="noopener noreferrer"
        className={`floating-beta-btn ${showFloatingNav ? 'floating-beta-btn--visible' : ''}`}
      >
        {t('hero.joinBeta')}
      </a>
    </div>
  );
}

export default HomePage;